import { decideBracketUrl } from '../../../shared/bracket-url';

/** ADR-002. The mat page polls about once a second. */
export const POLL_INTERVAL_MS = 1000;

/** The reconnecting mark appears once the last success is older than this. */
export const STALE_AFTER_MS = 5000;

export const LOST_SERVER = 'This page has lost the server.';
export const RECONNECTING = 'Reconnecting';
export const BOARD_UNSET = 'The board link is not set.';
export const BOARD_REFUSED = 'This board cannot be embedded.';
export const MAT_MISSING = 'This mat was not found.';

export interface MatPoll {
  tournament_name: string;
  mat_number: number;
  mat_name: string;
  bracket_url: string | null;
  remote_muted: boolean;
  notification: null;
}

export type MatPhase = 'loading' | 'missing' | 'offline' | 'ready';

export type EmbedBoard =
  | { kind: 'empty' }
  | { kind: 'embed'; url: string; host: string }
  | { kind: 'refused'; host: string | null };

export function pollPath(slug: string, number: string): string {
  return `/api/t/${encodeURIComponent(slug)}/mat/${encodeURIComponent(number)}`;
}

/** Keeps the six poll fields. Anything else in the payload is dropped. A non-null notification is not this feature. */
export function parseMatPoll(value: unknown): MatPoll | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.tournament_name !== 'string' || record.tournament_name.trim() === '') return null;
  if (typeof record.mat_name !== 'string' || record.mat_name.trim() === '') return null;
  if (typeof record.mat_number !== 'number' || !Number.isInteger(record.mat_number)) return null;
  if (record.mat_number < 1 || record.mat_number > 30) return null;
  if (record.bracket_url !== null && typeof record.bracket_url !== 'string') return null;
  if (typeof record.remote_muted !== 'boolean') return null;
  if ('notification' in record && record.notification !== null) return null;
  return {
    tournament_name: record.tournament_name,
    mat_number: record.mat_number,
    mat_name: record.mat_name,
    bracket_url: record.bracket_url,
    remote_muted: record.remote_muted,
    notification: null,
  };
}

/**
 * The iframe src is an allow-listed https URL only.
 * A refused link still yields a host when one can be parsed, without userinfo.
 */
export function embedBoard(bracketUrl: string | null): EmbedBoard {
  if (bracketUrl === null || bracketUrl.trim() === '') return { kind: 'empty' };
  const decision = decideBracketUrl(bracketUrl);
  if (decision.ok && decision.url) {
    return { kind: 'embed', url: decision.url, host: new URL(decision.url).hostname };
  }
  return { kind: 'refused', host: hostnameOf(bracketUrl) };
}

export function showReconnect(lastSuccessAt: number | null, now: number): boolean {
  if (lastSuccessAt === null) return false;
  return now - lastSuccessAt > STALE_AFTER_MS;
}

export function showLostServer(input: {
  hidden: boolean;
  phase: MatPhase;
  lastFailed: boolean;
  lastSuccessAt: number | null;
  now: number;
  startedAt: number;
}): boolean {
  if (input.phase === 'missing') return false;
  if (input.hidden) return true;
  if (input.phase === 'offline') return true;
  if (input.phase === 'loading' && input.now - input.startedAt > STALE_AFTER_MS) return true;
  return input.phase === 'ready' && input.lastFailed && showReconnect(input.lastSuccessAt, input.now);
}

export function matStatusText(stale: boolean, lost: boolean): string | null {
  if (!stale && !lost) return null;
  if (stale && lost) return `${RECONNECTING}. ${LOST_SERVER}`;
  if (stale) return RECONNECTING;
  return LOST_SERVER;
}

function hostnameOf(value: string): string | null {
  try {
    const host = new URL(value).hostname;
    return host || null;
  } catch {
    return null;
  }
}
